import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Coffee, ShieldCheck, Users, Mail } from "lucide-react";
import Navbar from "../components/Navbar";
import useSeo from "../hooks/useSeo";

/**
 * About — the "who is behind Moonbucks" page.
 * The audit flagged D3.3 Founder-led trust at 0/4 as the single biggest
 * trust gap. This page addresses the "who is behind this?" objection
 * head-on with the founder's name, face, and the "why I built this" story.
 *
 * TODO(founder): swap the [FOUNDER_STUB] blocks below with real content —
 * a real photo (upload to /public/team/), the actual LinkedIn URL, and the
 * final 150-word "why I built Moonbucks" paragraph. Analyst first names
 * and one-line credentials are also stubbed and should be filled in.
 */
export default function About() {
  useSeo({
    title: "About Moonbucks — Who we are",
    description: "The people behind Moonbucks. Why we built an analyst-vetted stock-alert service for busy retail investors.",
    canonical: "https://moonbucks.io/about",
  });

  return (
    <div className="bg-mb-sand min-h-screen">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-14">
        <Link to="/" className="inline-flex items-center gap-1 text-xs text-mb-emerald hover:underline mb-6" data-testid="about-back-link">
          <ArrowLeft className="w-3 h-3" /> Back to home
        </Link>

        <div className="label-uppercase text-mb-emerald mb-3">Who we are</div>
        <h1 className="font-display font-semibold text-4xl sm:text-5xl tracking-tight">
          The desk behind every Moonbucks alert.
        </h1>
        <p className="mt-4 text-neutral-600 text-lg max-w-2xl leading-relaxed">
          Moonbucks is a small team of former traders and quantitative analysts who got tired of watching retail investors get chewed up by pump-and-dump Discord groups and 2-hour-lagged newsletters. We built the tool we wished we had.
        </p>

        {/* Founder */}
        <section className="mt-14 bg-white border border-mb-border rounded-lg p-8 sm:p-10" data-testid="about-founder-card">
          <div className="grid sm:grid-cols-3 gap-8 items-start">
            <div className="sm:col-span-1">
              {/* [FOUNDER_STUB] Replace src with real founder photo at /public/team/founder.jpg */}
              <div
                className="w-full aspect-square rounded-lg bg-mb-emerald/10 border border-mb-border flex items-center justify-center overflow-hidden"
                data-testid="about-founder-photo"
              >
                <img
                  src="/team/founder.jpg"
                  alt="Founder of Moonbucks"
                  className="w-full h-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
                <Users className="w-16 h-16 text-mb-emerald/30 absolute" />
              </div>
              <div className="mt-3 text-[11px] text-neutral-400 italic">
                [Placeholder — founder photo pending upload]
              </div>
            </div>
            <div className="sm:col-span-2">
              <div className="label-uppercase text-mb-emerald">Founder</div>
              {/* [FOUNDER_STUB] Replace with real founder name */}
              <h2 className="font-display font-semibold text-2xl mt-1">[Founder Name]</h2>
              <p className="text-sm text-neutral-500 mt-0.5">[Ex-firm · Ex-role · Years trading]</p>
              <p className="mt-4 text-neutral-700 leading-relaxed">
                {/* [FOUNDER_STUB] Replace with real 150-word "why I built Moonbucks" paragraph */}
                I built Moonbucks because I watched too many friends turn a paycheck into a stop-loss chasing tips from a Discord server run by anonymous accounts. The stock market rewards discipline and information asymmetry — most retail tools sell you the opposite. Moonbucks is the version of that tool I wanted for my parents: analyst-reviewed, plain-English, and honest about every trade, whether it worked or not. Every alert we send has a paper trail. Every loss is public. If you want to know what a real, imperfect stock-alert service looks like, you're in the right place.
              </p>
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                {/* [FOUNDER_STUB] Replace with real LinkedIn URL */}
                <a href="https://www.linkedin.com/" target="_blank" rel="noreferrer" className="text-mb-emerald hover:underline" data-testid="about-founder-linkedin">LinkedIn →</a>
                <a href="mailto:hello@moonbucks.io" className="text-mb-emerald hover:underline">Email the desk →</a>
              </div>
            </div>
          </div>
        </section>

        {/* Analyst desk */}
        <section className="mt-8" data-testid="about-analysts">
          <div className="label-uppercase text-mb-emerald mb-3">The analyst desk</div>
          <h2 className="font-display font-semibold text-3xl tracking-tight">First names, real people, one job.</h2>
          <p className="mt-3 text-neutral-600 max-w-2xl">
            Every candidate our scanner surfaces gets eyeballed by a human before it reaches your inbox. The team is small on purpose — every alert is someone's name on the line.
          </p>
          <div className="mt-6 grid sm:grid-cols-3 gap-4">
            {/* [FOUNDER_STUB] Replace each analyst with real first name + one-line credential */}
            {[
              { name: "[Analyst 1]", role: "Ex-quant, 12 yrs equity signals" },
              { name: "[Analyst 2]", role: "Ex-prop trader, small-cap breakouts" },
              { name: "[Analyst 3]", role: "Ex-market-maker, tape reader" },
            ].map((a) => (
              <div key={a.name} className="bg-white border border-mb-border rounded-lg p-5">
                <div className="w-10 h-10 rounded-full bg-mb-emerald/10 flex items-center justify-center">
                  <Coffee className="w-5 h-5 text-mb-emerald" />
                </div>
                <div className="mt-3 font-display font-semibold text-lg">{a.name}</div>
                <div className="text-xs text-neutral-500 mt-0.5">{a.role}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Trust card */}
        <section className="mt-10 bg-mb-emerald/5 border border-mb-emerald rounded-lg p-6 sm:p-8">
          <div className="flex items-start gap-4">
            <ShieldCheck className="w-6 h-6 text-mb-emerald flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-display font-semibold text-xl">Every trade, every outcome — public.</h3>
              <p className="mt-2 text-sm text-neutral-700 leading-relaxed">
                We publish the full audit trail: every alert, entry price, exit price, and reason. Wins and losses. Nothing gets scrubbed if a call goes bad. That's the difference between an alert service and a track record.
              </p>
              <div className="mt-4 flex gap-3 text-sm">
                <Link to="/track-record" className="text-mb-emerald font-semibold hover:underline">See the track record →</Link>
                <Link to="/portfolio" className="text-mb-emerald font-semibold hover:underline">See the $10K simulation →</Link>
              </div>
            </div>
          </div>
        </section>

        <div className="mt-10 flex flex-col sm:flex-row items-center justify-between gap-4 py-6 border-t border-mb-border">
          <span className="text-sm text-neutral-500">Questions? We read every email.</span>
          <a href="mailto:hello@moonbucks.io" className="inline-flex items-center gap-2 text-sm text-mb-emerald font-semibold hover:underline" data-testid="about-contact-email">
            <Mail className="w-4 h-4" /> hello@moonbucks.io
          </a>
        </div>
      </div>
    </div>
  );
}
